/* eslint-disable react/prop-types */
// Small building blocks shared by all HR & Payroll screens.
import React, { useCallback, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Spin } from "antd";
import { RefreshCw } from "react-feather";
import { customStyleSm } from "../../../utils/common";
import { hrApi, useHrScope } from "../hrApi";
import { STATUS_COLORS } from "../hrConstants";

export const StatusBadge = ({ status }) => (
    <span className={`badge bg-${STATUS_COLORS[status] || "secondary"}`} style={{ fontSize: 11 }}>{status || "-"}</span>
);

/* ------------------------------------------------------------ page layout: the same on every HR screen */

// Header refresh: icon only, like the rest of the app
export const RefreshButton = ({ onClick, loading = false, disabled = false }) => (
    <button type="button" className="btn btn-added" title="Refresh" onClick={onClick} disabled={loading || disabled}>
        <RefreshCw size={16} className={loading ? "icon-spin" : ""} />
    </button>
);

// Legends / counts of a screen: under the header (never inside it), above the content
export const LegendBar = ({ children }) => (children ? <div className="hr-legend-bar">{children}</div> : null);

// The row of stat cards: spaced from the header and the table
export const StatRow = ({ children }) => <div className="row g-2 hr-stat-row">{children}</div>;

// A compact stat card (put them in a StatRow)
export const StatCard = ({ label, value, sub, color = "#4a5a7a", icon, loading = false }) => (
    <div className="card mb-0 h-100 hr-stat-card" style={{ borderLeft: `3px solid ${color}` }}>
        <div className="card-body d-flex align-items-center justify-content-between gap-2">
            <div style={{ minWidth: 0 }}>
                <div className="hr-stat-label text-truncate">{label}</div>
                <div className="hr-stat-value text-truncate" style={{ color }}>{loading ? <Spin size="small" /> : value}</div>
                {sub && <div className="hr-stat-sub text-truncate">{sub}</div>}
            </div>
            {icon && <div className="hr-stat-icon" style={{ color }}>{icon}</div>}
        </div>
    </div>
);

/* ------------------------------------------------------------ loading / error states */

// Full-area loader while a page's first data is on its way (the app's screen loader, components/ui/ScreenLoader.jsx)
export const PageLoader = ({ minHeight = 320 }) => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight }}>
        <Spin size="large" />
    </div>
);

// Shown when the first load failed; the screen stays usable via Try Again
export const LoadError = ({ message, onRetry, minHeight = 260 }) => (
    <div className="d-flex flex-column justify-content-center align-items-center gap-2 text-center" style={{ minHeight }}>
        <div className="fw-semibold" style={{ color: "#b91c1c" }}>Could not load data</div>
        <div className="text-muted" style={{ fontSize: 13, maxWidth: 480 }}>{message || "The server did not respond. Check your connection and try again."}</div>
        {onRetry && <button type="button" className="btn btn-outline-primary btn-sm mt-1" onClick={onRetry}><RefreshCw size={13} className="me-1" />Try Again</button>}
    </div>
);

// Button content: DMS spinner + text while busy
export const BusyLabel = ({ busy, text = "Loading...", children }) => (busy ? <><i className="fas fa-spinner fa-spin me-2" />{text}</> : children);

// One action at a time per screen: run(key, fn) ignores clicks while another action is running.
// busyKey tells which action is running so only that button shows a spinner.
export const useBusy = () => {
    const [busyKey, setBusyKey] = useState(null);
    const running = useRef(false);
    const mounted = useRef(true);
    // Set on every mount: React.StrictMode (dev) mounts, unmounts and mounts again, so a cleanup-only
    // effect left this false and the busy state was never cleared (buttons stuck disabled after an action)
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const run = useCallback(async (key, fn) => {
        if (running.current) return undefined;
        running.current = true;
        setBusyKey(key);
        try {
            return await fn();
        } finally {
            running.current = false;
            if (mounted.current) setBusyKey(null);
        }
    }, []);
    return [busyKey, run];
};

/* ------------------------------------------------------------ filters & dropdowns */

// Dealer filter, shown only to distributor users (dealer users are fixed to their own dealer)
export const DealerFilter = ({ value, onChange, allowAll = true, allLabel = "All Dealers", className = "field-200", label = "Dealer", required = false }) => {
    const { dealers, isDistributor } = useHrScope();
    if (!isDistributor) return null;
    const options = allowAll ? [{ value: 0, label: allLabel }, ...dealers] : dealers;
    return (
        <div className={className}>
            <label className="form-label">{required && "*"}{label}</label>
            <Select
                styles={customStyleSm}
                options={options}
                value={options.find((d) => d.value === value) || null}
                onChange={(o) => onChange(o?.value || 0)}
                placeholder={allowAll ? "All Dealers" : "Select Dealer"}
                isLoading={!dealers.length}
            />
        </div>
    );
};

export const SelectFilter = ({ label, options, value, onChange, className = "field-150", placeholder = "All", isClearable = true, isLoading = false }) => (
    <div className={className}>
        <label className="form-label">{label}</label>
        <Select
            styles={customStyleSm}
            options={options}
            value={options.find((o) => o.value === value) || null}
            onChange={(o) => onChange(o ? o.value : "")}
            placeholder={isLoading ? "Loading..." : placeholder}
            isClearable={isClearable}
            isLoading={isLoading}
            maxMenuHeight={250}
        />
    </div>
);

// Loads HR masters as react-select options. keys: departments | designations | grades | leaveTypes | payComponents
// dealerId: whose HR setup (designations, grades, leave types, pay components) to load; dealer users always get their own
export const useHrMasters = (keys = [], dealerId = 0) => {
    const [masters, setMasters] = useState({ departments: [], designations: [], grades: [], leaveTypes: [], payComponents: [], raw: {}, loading: keys.length > 0 });
    useEffect(() => {
        let alive = true;
        const loaders = {
            departments: () => hrApi.departments().then((r) => ({ raw: r.data || [], opts: (r.data || []).map((d) => ({ value: d.id, label: d.departmentName })) })),
            designations: () => hrApi.designations.getAll(dealerId).then((r) => ({ raw: r.data || [], opts: (r.data || []).filter((d) => d.isActive !== false).map((d) => ({ value: d.id, label: d.name })) })),
            grades: () => hrApi.grades.getAll(dealerId).then((r) => ({ raw: r.data || [], opts: (r.data || []).filter((d) => d.isActive !== false).map((d) => ({ value: d.id, label: d.name })) })),
            leaveTypes: () => hrApi.leaveTypes.getAll(dealerId).then((r) => ({ raw: r.data || [], opts: (r.data || []).filter((d) => d.isActive !== false).map((d) => ({ value: d.id, label: d.name })) })),
            payComponents: () => hrApi.payComponents.getAll(dealerId).then((r) => ({ raw: r.data || [], opts: (r.data || []).filter((d) => d.isActive !== false).map((d) => ({ value: d.id, label: `${d.name} (${d.type})` })) })),
        };
        setMasters((prev) => ({ ...prev, loading: keys.length > 0 }));
        Promise.all(keys.map((k) => loaders[k]().then((res) => [k, res]))).then((pairs) => {
            if (!alive) return;
            setMasters((prev) => {
                const next = { ...prev, raw: { ...prev.raw }, loading: false };
                pairs.forEach(([k, res]) => { next[k] = res.opts; next.raw[k] = res.raw; });
                return next;
            });
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keys.join(","), dealerId]);
    return masters;
};

// Employee options for a dealer (current employees by default)
export const useEmployeeOptions = (dealerId, status = "Current") => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        hrApi.employees({ dealerId, status }).then((r) => {
            if (!alive) return;
            setList(r.isSuccess ? r.data : []);
            setLoading(false);
        });
        return () => { alive = false; };
    }, [dealerId, status]);
    return {
        employees: list,
        loading,
        options: list.map((e) => ({ value: e.employeeId, label: `${e.employeeCode} - ${e.fullName}`, employee: e })),
    };
};

export const EmployeeSelect = ({ options, value, onChange, placeholder = "Select Employee", isDisabled = false, invalid = false, isLoading = false }) => (
    <Select
        styles={customStyleSm}
        className={invalid ? "is-invalid" : ""}
        options={options}
        value={options.find((o) => o.value === value) || null}
        onChange={(o) => onChange(o ? o.value : 0, o?.employee)}
        placeholder={isLoading ? "Loading employees..." : placeholder}
        noOptionsMessage={() => (isLoading ? "Loading..." : "No employees found")}
        isClearable
        isDisabled={isDisabled}
        isLoading={isLoading}
        maxMenuHeight={250}
    />
);

export const Amount = ({ value, bold = false }) => (
    <span style={{ fontWeight: bold ? 700 : 400 }}>{Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
);

export const fmtAmount = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
